import React from 'react';
import { cn } from '../../lib/utils';

interface StaleBadgeProps {
    isStale: boolean;
    onClick?: () => void;
    className?: string;
}

export function StaleBadge({ isStale, onClick, className }: StaleBadgeProps) {
    const badge = isStale ? (
        <span
            className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800',
                onClick && 'cursor-pointer hover:bg-amber-200 transition-colors',
                className,
            )}
            title="Scenario has been modified since last portfolio compute — click to review"
        >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Outdated
        </span>
    ) : (
        <span
            className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700',
                onClick && 'cursor-pointer hover:bg-green-100 transition-colors',
                className,
            )}
            title="Up to date — click to view approval history"
        >
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Up to date
        </span>
    );

    if (onClick) {
        return <button onClick={onClick} className="inline-flex">{badge}</button>;
    }
    return badge;
}

interface StaleBannerProps {
    staleCount: number;
    onReview: () => void;
}

export function StaleBanner({ staleCount, onReview }: StaleBannerProps) {
    if (staleCount === 0) return null;
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-amber-600 text-[20px]">warning</span>
                <span className="text-sm text-amber-800">
                    {staleCount} asset{staleCount !== 1 ? 's have' : ' has'} updated scenarios since last compute
                </span>
            </div>
            <button
                onClick={onReview}
                className="text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
                Review & Refresh
            </button>
        </div>
    );
}

interface PendingRecomputeBannerProps {
    count: number;
    onCompute: () => void;
    computing?: boolean;
}

export function PendingRecomputeBanner({ count, onCompute, computing }: PendingRecomputeBannerProps) {
    if (count === 0) return null;
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-amber-600 text-[20px]">calculate</span>
                <span className="text-sm text-amber-800">
                    {count} asset{count !== 1 ? 's' : ''} approved — recompute required to refresh portfolio results
                </span>
            </div>
            <button
                onClick={onCompute}
                disabled={computing}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors disabled:opacity-50"
            >
                {computing ? (
                    <div className="w-3.5 h-3.5 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                ) : (
                    <span className="material-symbols-outlined text-[16px]">calculate</span>
                )}
                Recompute now
            </button>
        </div>
    );
}

interface SettingsOutdatedBannerProps {
    onCompute: () => void;
    computing?: boolean;
}

export function SettingsOutdatedBanner({ onCompute, computing }: SettingsOutdatedBannerProps) {
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-amber-600 text-[20px]">tune</span>
                <span className="text-sm text-amber-800">
                    Group settings changed — KPIs below were computed with an older configuration. Recompute to refresh.
                </span>
            </div>
            <button
                onClick={onCompute}
                disabled={computing}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors disabled:opacity-50"
            >
                {computing ? (
                    <div className="w-3.5 h-3.5 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                ) : (
                    <span className="material-symbols-outlined text-[16px]">calculate</span>
                )}
                Recompute now
            </button>
        </div>
    );
}
